import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  championStrengthMeta,
  computeHealth,
  fmtCurrency,
  fmtRelative,
  healthBand,
  msaMeta,
  stageMeta,
  vendorMeta,
  ynMeta,
  type PipelineAccount,
} from '@/lib/pipelineAdapter';
import {
  Avatar,
  Chip,
  Field,
  HealthRing,
  Logo,
  Section,
  SourceChip,
  Stat,
} from './atoms';

interface SmartTableSidePanelProps {
  open: boolean;
  onClose: () => void;
  account: PipelineAccount | null;
}

function ReadValue({
  value,
  placeholder = 'Unknown',
}: {
  value: string | null | undefined;
  placeholder?: string;
}) {
  if (!value) {
    return (
      <span style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>
        {placeholder}
      </span>
    );
  }
  return (
    <span style={{ fontSize: 13, color: '#0f172a', whiteSpace: 'pre-line' }}>
      {value}
    </span>
  );
}

export function SmartTableSidePanel({
  open,
  onClose,
  account,
}: SmartTableSidePanelProps) {
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open || !account) {
    return null;
  }

  const a = account;
  const src = a.tableSources;
  const stage = stageMeta[a.stage];
  const score = computeHealth(a);
  const band = healthBand(score);
  const eb = a.economicBuyer;
  const ebConfBg =
    eb.confidence === 'confirmed'
      ? '#ecfdf5'
      : eb.confidence === 'tentative'
        ? '#fefce8'
        : '#f1f5f9';
  const ebConfFg =
    eb.confidence === 'confirmed'
      ? '#047857'
      : eb.confidence === 'tentative'
        ? '#a16207'
        : '#94a3b8';
  const ebSign = ynMeta[a.ebSignedOff];
  const budget = ynMeta[a.budgetApproved.value];
  const champ = a.champion.name ? championStrengthMeta[a.champion.strength] : null;
  const msa = msaMeta[a.msaTerms];
  const vendor = vendorMeta[a.vendorReg];
  const launch = a.launchDateAgreed;
  const launchFmt = launch.value
    ? new Date(launch.value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'auto',
        zIndex: 1000,
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.32)',
          opacity: 1,
          transition: 'opacity .2s',
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 480,
          background: 'white',
          boxShadow: '-20px 0 40px -12px rgba(15, 23, 42, 0.18)',
          transform: 'translateX(0)',
          transition: 'transform .28s cubic-bezier(.32,.72,.32,1)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 22px 14px',
            borderBottom: '1px solid #e8ebf0',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
          }}
        >
          <Logo color={a.logoColor} label={a.logo} size={42} radius={10} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 4,
              }}
            >
              <Chip dot={stage.dot} bg={stage.bg} color={stage.color} size="sm">
                {stage.label}
              </Chip>
              <span style={{ fontSize: 11, color: '#64748b' }}>
                · {fmtCurrency(a.arr)} ARR
              </span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#0f172a' }}>
              {a.name}
            </div>
            <div
              style={{
                fontSize: 12,
                color: '#64748b',
                marginTop: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
              }}
            >
              <span>Owner</span>
              <Avatar
                name={a.owner.name}
                color={a.owner.color}
                initials={a.owner.initials}
                size={14}
              />
              <span>{a.owner.name}</span>
              <span>· {a.calls} calls</span>
              {a.emails > 0 ? <span>· {a.emails} emails</span> : null}
              <span>· last {fmtRelative(a.lastActivity)}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: '#64748b',
              padding: 4,
              fontSize: 18,
              lineHeight: 1,
            }}
            aria-label="Close panel"
          >
            ✕
          </button>
        </div>

        {/* Health summary */}
        <div
          style={{
            padding: '14px 22px',
            borderBottom: '1px solid #e8ebf0',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            background: '#fafbfc',
          }}
        >
          <HealthRing score={score} size={48} stroke={4} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>
              Deal health
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: band.color }}>
              {band.label}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 14 }}>
            <Stat label="Momentum" value={`${a.momentum}/5`} />
            <Stat label="Strength" value={`${a.strength}/5`} />
            <Stat
              label="Blockers"
              value={a.blockers.length}
              accent={a.blockers.length ? '#b91c1c' : '#64748b'}
            />
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 22px 24px' }}>
          <Section title="MEDDPICC">
            <Field label="Economic Buyer" source={src?.economicBuyer}>
              <ReadValue value={eb.value} placeholder="Not identified" />
              <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center' }}>
                <Chip size="sm" bg={ebConfBg} color={ebConfFg} dot={ebConfFg}>
                  {eb.confidence}
                </Chip>
                <Chip size="sm" bg={ebSign.bg} color={ebSign.fg} dot={ebSign.dot}>
                  signed off · {ebSign.label}
                </Chip>
                {src?.economicBuyerSignedOff && (
                  <SourceChip kind={src.economicBuyerSignedOff} />
                )}
              </div>
              {eb.note && (
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                  {eb.note}
                </div>
              )}
            </Field>

            <Field label="Use case" source={src?.useCase}>
              <ReadValue value={a.useCase} />
            </Field>

            <Field label="Launch date agreed" source={src?.tentativeLaunchDateAgreed}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                {launchFmt ? (
                  <span style={{ fontSize: 13, color: '#0f172a' }}>{launchFmt}</span>
                ) : (
                  <span
                    style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}
                  >
                    Unset
                  </span>
                )}
                <Chip
                  size="sm"
                  bg={launch.status === 'agreed' ? '#ecfdf5' : '#fefce8'}
                  color={launch.status === 'agreed' ? '#047857' : '#a16207'}
                >
                  {launch.status}
                </Chip>
              </div>
            </Field>

            <Field label="Budget approved" source={src?.budgetApprovedByEB}>
              <Chip size="sm" bg={budget.bg} color={budget.fg} dot={budget.dot}>
                {budget.label}
              </Chip>
              {a.budgetApproved.note && (
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                  {a.budgetApproved.note}
                </div>
              )}
            </Field>
          </Section>

          <Section
            title={
              src?.potentialBlockers && src.potentialBlockers !== 'none' ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span>{`Blockers · ${a.blockers.length}`}</span>
                  <SourceChip kind={src.potentialBlockers} />
                </span>
              ) : (
                `Blockers · ${a.blockers.length}`
              )
            }
          >
            {a.blockers.length === 0 && (
              <div style={{ fontSize: 13, color: '#94a3b8' }}>None recorded.</div>
            )}
            {a.blockers.map((b, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                  padding: '8px 10px',
                  background: '#fef2f2',
                  borderRadius: 6,
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: '#ef4444',
                    marginTop: 7,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    color: '#7f1d1d',
                    lineHeight: 1.45,
                  }}
                >
                  {b}
                </span>
              </div>
            ))}
          </Section>

          <Section
            title={
              src?.agreedNextSteps && src.agreedNextSteps !== 'none' ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span>{`Next steps · ${a.nextSteps.length}`}</span>
                  <SourceChip kind={src.agreedNextSteps} />
                </span>
              ) : (
                `Next steps · ${a.nextSteps.length}`
              )
            }
          >
            {a.nextSteps.length === 0 && (
              <div style={{ fontSize: 13, color: '#94a3b8' }}>None recorded.</div>
            )}
            {a.nextSteps.map((s, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                  padding: '8px 10px',
                  background: '#fafbfc',
                  borderRadius: 6,
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    border: '1.5px solid #cbd5e1',
                    borderRadius: 4,
                    marginTop: 2,
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 12, color: '#334155', lineHeight: 1.45 }}>
                  {s}
                </span>
              </div>
            ))}
          </Section>

          {a.redFlags.length > 0 && (
            <Section title={`Red flags · ${a.redFlags.length}`}>
              {a.redFlags.map((f, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start',
                    padding: '8px 10px',
                    background: '#fef2f2',
                    borderRadius: 6,
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: '#dc2626',
                      marginTop: 7,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 12, color: '#7f1d1d', lineHeight: 1.45 }}>
                    {f}
                  </span>
                </div>
              ))}
            </Section>
          )}

          <Section title="People">
            <Field label="Champion" source={src?.champion}>
              {a.champion.name ? (
                <>
                  <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>
                    {a.champion.name}
                  </div>
                  {a.champion.role && (
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                      {a.champion.role}
                    </div>
                  )}
                  {champ && (
                    <Chip
                      size="sm"
                      bg={champ.bg}
                      color={champ.fg}
                      dot={champ.dot}
                      style={{ marginTop: 6 }}
                    >
                      {champ.label}
                    </Chip>
                  )}
                </>
              ) : (
                <ReadValue value={null} placeholder="Missing" />
              )}
            </Field>
            <Field label="Legal contact" source={src?.legalContact}>
              <ReadValue value={a.legalContact.name} placeholder="Missing" />
            </Field>
            <Field label="Procurement contact" source={src?.procurementContact}>
              <ReadValue value={a.procurementContact.name} placeholder="Missing" />
            </Field>
          </Section>

          <Section title="Paperwork">
            <Field label="MSA / Terms" source={src?.msaStatus}>
              <Chip size="sm" bg={msa.bg} color={msa.fg}>
                {msa.label}
              </Chip>
            </Field>
            <Field label="Vendor registration" source={src?.vendorRegistration}>
              <Chip size="sm" bg={vendor.bg} color={vendor.fg}>
                {vendor.label}
              </Chip>
            </Field>
          </Section>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 22px',
            borderTop: '1px solid #e8ebf0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            background: 'white',
          }}
        >
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            Read-only · edits happen during account analysis
          </span>
          <Link
            to={`/accounts/${a.id}`}
            onClick={onClose}
            style={{
              padding: '7px 14px',
              fontSize: 13,
              fontWeight: 600,
              color: 'white',
              border: 'none',
              background: '#3730a3',
              borderRadius: 6,
              textDecoration: 'none',
            }}
          >
            Open full account
          </Link>
        </div>
      </div>
    </div>
  );
}
